import React, { Component } from "react";
import axios from "axios";
import LineDiagram from "./graph/LineDiagram";
import { Utils } from "../Utils";

// The preview pairs velocity and TEC, so it only scans the constellations that
// have both — QZSS and the BeiDou GEO produce TEC only (see constellations.ts).
import {
  VELOCITY_CONSTELLATIONS as CONSTELLATIONS,
  Constellation,
  CONS_SLUG,
  satelliteName,
} from "../constellations";

interface Props {
  station: any;
}

interface State {
  varionVel: any[];
  varionIon: any[];
  isStationOffline: boolean;
  liveConst: string | null;
  liveSat: number | null;
  intervalVel: ReturnType<typeof setInterval> | null;
  intervalIono: ReturnType<typeof setInterval> | null;
}

class HomePreviewDash extends Component<Props, State> {
  private discovering = false;

  constructor(props: any) {
    super(props);
    this.state = {
      varionVel: [],
      varionIon: [],
      isStationOffline: false,
      liveConst: null,
      liveSat: null,
      intervalVel: null,
      intervalIono: null,
    };
  }

  private stationId() {
    return this.props.station._id.toLowerCase();
  }

  /** Try each constellation until we find one with a live satellite */
  private async discoverLive() {
    if (this.discovering) return;
    this.discovering = true;
    const base = Utils.BACKEND_BASE_URL;

    for (const c of CONSTELLATIONS) {
      try {
        const res = await axios.get(
          `${base}station/${this.stationId()}/constellation/${c}/satellites/status`
        );
        const status: Record<string, boolean> = res.data;
        const liveSat = Object.keys(status).find((k) => status[k]);
        if (liveSat) {
          this.discovering = false;
          return { constellation: c, satellite: parseInt(liveSat) };
        }
      } catch {
        // constellation not available, try next
      }
    }
    this.discovering = false;
    return null;
  }

  private async startPolling() {
    this.stopPolling();

    const live = await this.discoverLive();
    if (!live) {
      this.setState({ isStationOffline: true, varionVel: [], varionIon: [], liveConst: null, liveSat: null });
      return;
    }

    this.setState({ liveConst: live.constellation, liveSat: live.satellite, isStationOffline: false });

    this.fetchVelocity(live.constellation);
    this.fetchIono(live.constellation, live.satellite);

    const intervalVel = setInterval(() => this.fetchVelocity(live.constellation), 1000);
    const intervalIono = setInterval(() => this.fetchIono(live.constellation, live.satellite), 1000);
    this.setState({ intervalVel, intervalIono });
  }

  private stopPolling() {
    if (this.state.intervalVel) clearInterval(this.state.intervalVel);
    if (this.state.intervalIono) clearInterval(this.state.intervalIono);
    this.setState({ intervalVel: null, intervalIono: null });
  }

  private fetchVelocity(constellation: string) {
    axios
      .get(
        `${Utils.BACKEND_BASE_URL}station/${this.stationId()}/constellation/${constellation}?limit=12`
      )
      .then((res) => {
        if (Array.isArray(res.data)) {
          this.setState({ varionVel: res.data, isStationOffline: false });
        } else {
          this.setState({ isStationOffline: true, varionVel: [] });
        }
      })
      .catch(() => this.setState({ isStationOffline: true, varionVel: [] }));
  }

  private fetchIono(constellation: Constellation, satNum: number) {
    const slug = CONS_SLUG[constellation];
    axios
      .get(
        `${Utils.BACKEND_BASE_URL}station/${this.stationId()}/satellite/${slug}/number/${satNum}?limit=12`
      )
      .then((res) => {
        if (Array.isArray(res.data)) {
          this.setState({ varionIon: res.data, isStationOffline: false });
        } else {
          this.setState({ isStationOffline: true, varionIon: [] });
        }
      })
      .catch(() => this.setState({ isStationOffline: true, varionIon: [] }));
  }

  componentDidMount() {
    this.startPolling();
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.station._id !== this.props.station._id) {
      this.startPolling();
    }
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  render() {
    const { varionVel, varionIon, isStationOffline, liveConst, liveSat } = this.state;

    const constLabel = liveConst ?? "—";
    const satLabel = liveConst && liveSat != null ? satelliteName(liveConst, liveSat) : "";

    let upData: any = null;
    if (varionVel.length > 0) {
      upData = [
        {
          id: this.props.station._id,
          color: Utils.LINE_DIAGRAM_COLOR,
          data: varionVel.slice().reverse().map((c: any) => ({ x: c.t, y: c.up * 100 })),
        },
      ];
    }

    let ionoData: any = null;
    if (varionIon.length > 0) {
      ionoData = [
        {
          id: this.props.station._id,
          color: Utils.LINE_DIAGRAM_COLOR,
          data: varionIon.slice().reverse().map((c: any) => ({ x: c.t, y: c.tec })),
        },
      ];
    }

    return (
      <div className="space-y-3">
        {!isStationOffline && (
          <>
            {upData && (
              <div>
                <h6 className="text-xs font-semibold text-brand-400 mb-1">
                  Ground Preview — Up velocity
                  <span className="ml-1 text-gray-400 font-normal">({constLabel})</span>
                </h6>
                <div className="chart-area-sm"><LineDiagram data={upData} /></div>
              </div>
            )}
            {ionoData && (
              <div>
                <h6 className="text-xs font-semibold text-brand-400 mb-1">
                  Ionosphere preview — dsTEC/dt
                  <span className="ml-1 text-gray-400 font-normal">({constLabel} · {satLabel})</span>
                </h6>
                <div className="chart-area-sm">
                  <LineDiagram data={ionoData} legend="tecu/s" />
                </div>
              </div>
            )}
          </>
        )}
        {isStationOffline && (
          <p className="text-xs text-gray-400 italic text-center py-4">
            Station is offline
          </p>
        )}
      </div>
    );
  }
}

export default HomePreviewDash;
